import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, AlertOctagon, TrendingUp, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Widget Alert Kesehatan Cabang
 * - Fetches /api/v2/dashboard/branch-health?branch_id=X
 * - Shows full-width banner when isolir ratio > 30%
 * - Orange for "warning" (30-49%), Red for "critical" (>=50%)
 * - Supports light & dark variants
 */
const BranchHealthAlert = ({ selectedBranch, variant = 'light' }) => {
  const [health, setHealth] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
    const token = localStorage.getItem('token');
    const branchQ = (selectedBranch && selectedBranch !== 'all') ? `?branch_id=${selectedBranch}` : '';
    axios.get(`${API_URL}/api/v2/dashboard/branch-health${branchQ}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then((res) => {
      if (res.data?.success) setHealth(res.data.data);
    }).catch(() => setHealth(null));
  }, [selectedBranch]);

  if (!health || dismissed) return null;
  if (health.status !== 'warning' && health.status !== 'critical') return null;

  const isCritical = health.status === 'critical';
  const isDark = variant === 'dark';

  // Color tokens
  const accent = isCritical ? '#ef4444' : '#f97316';
  const accentSoft = isCritical ? 'rgba(239, 68, 68, 0.15)' : 'rgba(249, 115, 22, 0.15)';
  const accentBorder = isCritical ? 'rgba(239, 68, 68, 0.45)' : 'rgba(249, 115, 22, 0.45)';
  const Icon = isCritical ? AlertOctagon : AlertTriangle;

  const bgStyle = isDark
    ? {
        background: `linear-gradient(90deg, ${accentSoft} 0%, rgba(15, 23, 42, 0.85) 100%)`,
        border: `1px solid ${accentBorder}`,
        backdropFilter: 'blur(16px)',
      }
    : {
        background: `linear-gradient(90deg, ${accentSoft} 0%, rgba(255, 255, 255, 0.85) 100%)`,
        border: `1px solid ${accentBorder}`,
        backdropFilter: 'blur(16px)',
        boxShadow: `0 8px 28px -6px ${accentSoft}`,
      };

  const textPrimary = isDark ? 'text-slate-100' : 'text-slate-900';
  const textSecondary = isDark ? 'text-slate-300' : 'text-slate-700';
  const textMuted = isDark ? 'text-slate-400' : 'text-slate-600';

  // Mini sparkline from trend_7days
  const trend = Array.isArray(health.trend_7days) ? health.trend_7days : [];
  const trendMax = Math.max(1, ...trend.map(t => t.count || 0));

  const label = isCritical ? 'KRITIS' : 'PERHATIAN';
  const headline = isCritical
    ? `Rasio isolir cabang mencapai ${health.isolir_ratio}% — Tindakan segera diperlukan!`
    : `Rasio isolir cabang ${health.isolir_ratio}% — Di atas ambang aman (30%)`;

  const scopeLabel = health.branch_name
    ? `Cabang: ${health.branch_name}`
    : 'Seluruh Cabang';

  return (
    <div
      data-testid="branch-health-alert"
      className="relative rounded-2xl px-4 sm:px-5 py-3.5 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-500"
      style={bgStyle}
    >
      {/* Pulse dot for critical */}
      {isCritical && (
        <span
          className="absolute top-3 right-12 w-2.5 h-2.5 rounded-full animate-ping"
          style={{ background: accent }}
        />
      )}

      <div className="flex items-start gap-3 sm:gap-4">
        {/* Icon */}
        <div
          className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center"
          style={{ background: accentSoft, color: accent }}
        >
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{ background: accent, color: '#fff' }}
              data-testid="branch-health-badge"
            >
              {label}
            </span>
            <span className={`text-[11px] font-medium ${textMuted}`}>
              {scopeLabel}
            </span>
          </div>

          <div className={`text-sm sm:text-base font-semibold leading-snug ${textPrimary}`} data-testid="branch-health-headline">
            {headline}
          </div>

          <div className={`text-xs mt-1.5 flex flex-wrap gap-x-4 gap-y-1 ${textSecondary}`}>
            <span>
              <strong className={textPrimary}>{health.isolated_count}</strong> / {health.total_customers} pelanggan terisolir
            </span>
            {typeof health.overdue_count === 'number' && (
              <span>
                <strong className={textPrimary}>{health.overdue_count}</strong> belum bayar
              </span>
            )}
            {typeof health.active_customers === 'number' && (
              <span>
                <strong className={textPrimary}>{health.active_customers}</strong> aktif
              </span>
            )}
          </div>
        </div>

        {/* Trend sparkline (hidden on mobile) */}
        {trend.length > 0 && (
          <div className="hidden md:flex items-end gap-0.5 h-10 pr-1" title="Tren isolir 7 hari terakhir">
            {trend.map((t, i) => {
              const h = Math.max(4, (t.count / trendMax) * 36);
              return (
                <div
                  key={i}
                  className="w-1.5 rounded-sm transition-all"
                  style={{
                    height: `${h}px`,
                    background: accent,
                    opacity: 0.4 + (0.6 * (t.count / trendMax)),
                  }}
                  title={`${t.day}: ${t.count}`}
                />
              );
            })}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to="/app/isolir-management"
            data-testid="branch-health-cta"
            className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-all hover:scale-105"
            style={{ background: accent, color: '#fff' }}
          >
            <TrendingUp className="w-3 h-3" />
            Tinjau
          </Link>
          <button
            onClick={() => setDismissed(true)}
            data-testid="branch-health-dismiss"
            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
              isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-black/5 text-slate-500'
            }`}
            aria-label="Tutup"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BranchHealthAlert;
